declare global {
  interface Window {
    sharedBus: any;
  }
}

import type { WidgetFormValues, WidgetTypeValue, WidgetVariantValue } from '~/types/widget';
// The pure widget-config builder lives in shared constants so merchant-svc (store onboarding) and
// this frontend compile identical default config JSON. Re-exported below to preserve the existing
// `cart-widget-helpers` import paths used across valet.
import { buildDefaultWidgetFormValues } from '@constants';
import { getPreviewCartData } from './cart-preview-data';
import { ref, watch, type Ref } from 'vue';
import { useCustomizationsStore } from '../store/customizations';

export { compileWidgetConfig, updateCheckoutConfigWithFormValues, generateWidgetName } from '@constants';

/**
 * Create initial widget form values with defaults and customizations. Thin wrapper over the shared
 * `buildDefaultWidgetFormValues` that injects the only frontend-coupled input — `showStoreCredit`,
 * which comes from the customizations Pinia store.
 */
export function createInitialWidgetFormValues(
  customizations?: Partial<WidgetFormValues>,
  widgetType: WidgetTypeValue = 'CART',
  variant: WidgetVariantValue = 'toggle',
): WidgetFormValues {
  const customizationStore = useCustomizationsStore();

  return buildDefaultWidgetFormValues({
    customizations,
    showStoreCredit: customizationStore.showStoreCredit,
    variant,
    widgetType,
  });
}

export function normalizeFormValues(values: WidgetFormValues | null) {
  if (!values) return values;

  const normalized = { ...values };

  // Handle expected differences that shouldn't trigger unsaved changes
  Object.keys(normalized).forEach(key => {
    const value = normalized[key];
    if (value === undefined) {
      normalized[key] = '';
    }
    if (value === null) {
      normalized[key] = '';
    }
  });

  return normalized;
}

// Mock store-credit settings for the widget preview. The cart-widget's store-credit element
// derives its value from a `settings/loaded` bus event; in the real storefront the widget fetches
// these settings itself, but the customizer preview has to feed them in.
const PREVIEW_WIDGET_SETTINGS = {
  storeCreditRules: [
    {
      max: 1000,
      min: 100,
      value: 100,
    },
  ],
  storeCreditValue: 5,
};

/**
 * (Re)dispatch the mock store-credit settings to the cart-widget preview.
 *
 * The `<order-protection>` element re-renders its shadow DOM every time its `config` attribute is
 * set (which the editor does on every edit). The store-credit element subscribes to `settings/loaded`
 * fresh on each render, so unless the settings are re-provided after a config push the store-credit
 * value falls back to its baked default — or disappears entirely for configs without one. Re-dispatch
 * after each config push keeps it stable. No-ops until the widget script has published `sharedBus`.
 */
export function dispatchWidgetPreviewSettings() {
  if (typeof window === 'undefined') return;
  const dispatch = () => window.sharedBus?.dispatch('settings/loaded', PREVIEW_WIDGET_SETTINGS);
  // The <order-protection> element re-renders its shadow DOM asynchronously after a config change,
  // and the store-credit element re-subscribes to `settings/loaded` during that render — later than
  // a Vue tick. Dispatch across a few frames so the settings land whenever the (variable-timing)
  // re-render finishes; the payload is identical each time, so extra dispatches are no-ops.
  requestAnimationFrame(dispatch);
  setTimeout(dispatch, 100);
  setTimeout(dispatch, 300);
}

/**
 * Sample warranty tiers for the coverage selector preview, priced off the $100 preview cart. The
 * cart-widget only uses them when the store's own pricing offers no tier, so a merchant can style
 * the warranty chips before their extended warranty pricing is live.
 */
export const PREVIEW_WARRANTY_OPTIONS = [
  { duration: 1, price: 699 },
  { duration: 2, price: 1199 },
];

export function enableWarrantyPreview() {
  if (typeof window === 'undefined') return;
  (window as unknown as { __OP_WIDGET_PREVIEW__?: unknown }).__OP_WIDGET_PREVIEW__ = {
    warrantyOptions: PREVIEW_WARRANTY_OPTIONS,
  };
}

/**
 * Whether the preview bundle is on its way, running, or not deployed for this store.
 * `unavailable` is a legitimate resting state: a brand with no CDN deploy has no bundle to show,
 * and serving another brand's is what this branch removed.
 */
export type WidgetPreviewStatus = 'pending' | 'loaded' | 'unavailable';

/**
 * Set up cart helper mock data and widget script: mocks /cart.js and loads the widget bundle.
 *
 * @param scriptSrc the bundle to preview — pass `useCustomizationsStore().scriptSourceUrl` so the
 * merchant previews the code their own storefront runs. Deliberately required and un-defaulted: a
 * hardcoded brand slug here is what froze the preview at another brand's deploy, previewing a
 * recoloured logo as a solid disc and `child-flex` as nothing.
 * @param onStatus reports whether the bundle loaded, so the preview can explain an empty pane.
 */
export function setupCartHelper(scriptSrc: string, onStatus?: (status: WidgetPreviewStatus) => void) {
  // Set up cart helper mock data
  const cartData = getPreviewCartData();
  enableWarrantyPreview();

  const json = (body: unknown) =>
    Promise.resolve(
      new Response(JSON.stringify(body), { headers: { 'Content-Type': 'application/json' }, status: 200 }),
    );

  // Override the storefront calls the script makes: /cart.js for the cart, and the coverage
  // selector's cart write and warranty eligibility, which must never reach a real store from here.
  const originalFetch = window.fetch;
  window.fetch = function (url, options) {
    if (typeof url === 'string' && url.includes('/cart.js')) return json(cartData);
    if (typeof url === 'string' && url.includes('/cart/update.js')) return json(cartData);
    if (typeof url === 'string' && url.includes('/v1/quote/warranty-eligibility')) return json({ products: {} });

    return originalFetch(url, options);
  };

  // Load widget script.
  //
  // The cart-widget bundle is UMD. This editor also loads Monaco (the Advanced Config code
  // editor), whose AMD loader installs a global `define` with `.amd`. A UMD wrapper prefers
  // AMD when it sees `define.amd`, so the bundle registers itself as an anonymous module
  // instead of running its browser-global init — `window.sharedBus` and the custom elements
  // never get defined and the preview renders nothing (Monaco even throws "Can only have one
  // anonymous define call per script file").
  //
  // Monaco may install its `define` BEFORE or AFTER this runs (load-order race), so we can't
  // just snapshot-and-hide. Instead, trap `define` for the duration of the script's load:
  // reads report `undefined` (the bundle takes the global-script branch), writes are captured,
  // and the captured loader is restored once the script has executed — so Monaco is unaffected.
  const globalScope = window as unknown as { define?: unknown };
  let heldDefine: unknown = globalScope.define;
  let released = false;
  const releaseDefine = () => {
    if (released) return;
    released = true;
    try {
      Object.defineProperty(globalScope, 'define', {
        configurable: true,
        enumerable: true,
        value: heldDefine,
        writable: true,
      });
    } catch {
      globalScope.define = heldDefine;
    }
  };
  try {
    Object.defineProperty(globalScope, 'define', {
      configurable: true,
      get: () => (released ? heldDefine : undefined),
      set: value => {
        heldDefine = value;
      },
    });
  } catch {
    // Non-configurable `define` (unexpected): best-effort snapshot hide instead.
    heldDefine = globalScope.define;
    globalScope.define = undefined;
  }

  // Without a resolvable bundle there is nothing correct to load, and guessing means loading some
  // other brand's widget. Leave the preview empty instead.
  if (!scriptSrc) {
    releaseDefine();
    onStatus?.('unavailable');

    return;
  }

  const script = document.createElement('script');
  // Every brand slug on the CDN is its own deploy, so a slug that is not this merchant's freezes
  // the preview at whenever THAT brand last shipped: `imp-station-2` predates the knockout-sparing
  // recolour and `child-flex`, so a recoloured logo previewed as a solid disc and "Share the row
  // evenly" previewed as nothing — both correct on the merchant's own, newer bundle.
  script.src = scriptSrc;
  script.async = true;
  script.addEventListener('load', () => {
    releaseDefine();
    onStatus?.('loaded');
  });
  // A brand whose bundle has never been deployed 404s here. Reported rather than swallowed, so the
  // preview can say why it is empty instead of just being empty.
  script.addEventListener('error', () => {
    releaseDefine();
    onStatus?.('unavailable');
  });
  document.body.appendChild(script);
  // Safety net: restore even if neither load nor error fires, so Monaco is never left without
  // its loader for longer than the script's fetch+execute.
  setTimeout(releaseDefine, 5000);

  // The controller uses sharedBus internally, so you can use it directly
  setTimeout(() => {
    dispatchWidgetPreviewSettings();
  }, 1000);
}

/**
 * Loads the preview bundle the moment the store's URL is known, and only once.
 *
 * `scriptSourceUrl` is '' until `currentStore` has resolved, and every caller used to fire
 * `setupCartHelper` a single time from `onMounted`. A page that mounted first therefore got the
 * empty-src early return and nothing ever came back to load the bundle — a blank preview until the
 * next full reload. Watching until the first non-empty URL closes that gap without loading twice.
 */
export function loadWidgetPreviewWhenReady(getSrc: () => string): Ref<WidgetPreviewStatus> {
  const status = ref<WidgetPreviewStatus>('pending');
  let loaded = false;
  const stop = watch(
    getSrc,
    src => {
      if (!src || loaded) return;
      loaded = true;
      setupCartHelper(src, next => (status.value = next));
      // `immediate` invokes this synchronously inside watch(), before `stop` is initialised, so the
      // teardown is deferred a microtask rather than referenced here directly.
      queueMicrotask(() => stop());
    },
    { immediate: true },
  );

  return status;
}
